import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { API_ENDPOINTS } from '../utils/constants';
import { formatDate } from '../utils/helpers';
import StatsTab from '../components/admin/StatsTab';

function AdminPanel() {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [reports, setReports] = useState([]);
  const [content, setContent] = useState([]);
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [contentFilter, setContentFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('reports');

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('qdn_access_token');
      const typeParam = filter !== 'all' ? `?type=${filter}` : '';
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(
        `${API_BASE_URL}/api${API_ENDPOINTS.ADMIN.REPORTS}${typeParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setReports(data.reports || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      showError(`Failed to load reports: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [filter, showError]);

  const fetchContent = useCallback(async () => {
    try {
      setContentLoading(true);
      const token = localStorage.getItem('qdn_access_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      
      const response = await fetch(
        `${API_BASE_URL}/api${API_ENDPOINTS.CONTENT.LIST}?limit=100`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch content');
      }

      const data = await response.json();
      setContent(data.content || []);
    } catch (error) {
      console.error('Error fetching content:', error);
      showError(`Failed to load content: ${error.message}`);
    } finally {
      setContentLoading(false);
    }
  }, [showError]);

  const fetchActivities = useCallback(async () => {
    try {
      setActivityLoading(true);
      const token = localStorage.getItem('qdn_access_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      
      const response = await fetch(
        `${API_BASE_URL}/api${API_ENDPOINTS.ACTIVITY.LIST}?limit=100`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch activities');
      }

      const data = await response.json();
      setActivities(data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
      showError(`Failed to load activities: ${error.message}`);
    } finally {
      setActivityLoading(false);
    }
  }, [showError]);

  const fetchUsers = useCallback(async () => {
    try {
      setUserLoading(true);
      const token = localStorage.getItem('qdn_access_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      
      const response = await fetch(
        `${API_BASE_URL}/api${API_ENDPOINTS.ADMIN.USERS}?limit=200`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      showError(`Failed to load users: ${error.message}`);
    } finally {
      setUserLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    if (activeTab === 'content') {
      fetchContent();
    }
  }, [activeTab, fetchContent]);

  useEffect(() => {
    if (activeTab === 'community') {
      fetchActivities();
    }
  }, [activeTab, fetchActivities]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab, fetchUsers]);

  const handleHideItem = async (itemId, itemType, isCurrentlyHidden) => {
    try {
      const token = localStorage.getItem('qdn_access_token');
      const endpoint = itemType === 'content' 
        ? API_ENDPOINTS.CONTENT.HIDE(itemId)
        : API_ENDPOINTS.ACTIVITY.HIDE(itemId);

      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE_URL}/api${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isHidden: !isCurrentlyHidden }),
      });

      if (!response.ok) {
        throw new Error('Failed to update item visibility');
      }

      const action = isCurrentlyHidden ? 'unhidden' : 'hidden';
      showSuccess(`Item ${action} successfully`);
      
      // Refresh the appropriate list
      if (activeTab === 'reports') {
        fetchReports();
      } else if (activeTab === 'content') {
        fetchContent();
      } else if (activeTab === 'community') {
        fetchActivities();
      }
    } catch (error) {
      console.error('Error updating item:', error);
      showError('Failed to update item visibility');
    }
  };

  const handleDeleteItem = async (itemId, itemType) => {
    if (!window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('qdn_access_token');
      const endpoint = itemType === 'content' 
        ? API_ENDPOINTS.CONTENT.DELETE(itemId)
        : API_ENDPOINTS.ACTIVITY.DELETE(itemId);

      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE_URL}/api${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete item');
      }

      showSuccess('Item deleted successfully');
      
      // Refresh the appropriate list
      if (activeTab === 'content') {
        fetchContent();
      } else if (activeTab === 'community') {
        fetchActivities();
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      showError('Failed to delete item');
    }
  };

  const filteredContent = content.filter(item => {
    if (contentFilter === 'all') return true;
    if (contentFilter === 'published') return item.isPublished;
    if (contentFilter === 'hidden') return item.isHidden;
    return item.type === contentFilter;
  });

  const filteredActivities = activities.filter(item => {
    if (activityFilter === 'all') return true;
    if (activityFilter === 'hidden') return item.isHidden;
    return item.type === activityFilter;
  });

  const filteredUsers = users.filter(user => {
    if (userFilter === 'all') return true;
    if (userFilter === 'admin') return user.roles && user.roles.includes('admin');
    if (userFilter === 'moderator') return user.roles && user.roles.includes('moderator');
    if (userFilter === 'member') return user.roles && user.roles.includes('member') && !user.roles.includes('admin') && !user.roles.includes('moderator');
    return true;
  });

  const handleRoleChange = async (userId, currentRoles, roleToToggle) => {
    try {
      const token = localStorage.getItem('qdn_access_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      
      // Toggle the role
      let newRoles = [...currentRoles];
      if (newRoles.includes(roleToToggle)) {
        newRoles = newRoles.filter(r => r !== roleToToggle);
      } else {
        newRoles.push(roleToToggle);
      }
      
      // Ensure at least 'member' role
      if (!newRoles.length || (!newRoles.includes('member') && !newRoles.includes('admin') && !newRoles.includes('moderator'))) {
        newRoles.push('member');
      }
      
      const response = await fetch(
        `${API_BASE_URL}/api${API_ENDPOINTS.ADMIN.UPDATE_USER_ROLE(userId)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ roles: newRoles }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update user role');
      }

      showSuccess('User role updated successfully');
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error updating user role:', error);
      showError('Failed to update user role');
    }
  };

  // Check if user is admin
  if (!user || !user.roles || !user.roles.includes('admin')) {
    return (
      <div className="page">
        <div className="container">
          <div className="content-page">
            <h1>Access Denied</h1>
            <p>You do not have permission to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        <div className="content-page">
          <h1>Admin Panel</h1>

          {/* Tab Navigation */}
          <div className="admin-tabs">
            <button
              className={`tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
              onClick={() => setActiveTab('stats')}
            >
              Statistics
            </button>
            <button
              className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
              onClick={() => setActiveTab('reports')}
            >
              Moderation Reports
            </button>
            <button
              className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
              onClick={() => setActiveTab('content')}
            >
              Resource Management
            </button>
            <button
              className={`tab-btn ${activeTab === 'community' ? 'active' : ''}`}
              onClick={() => setActiveTab('community')}
            >
              Community Management
            </button>
            <button
              className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              User Management
            </button>
          </div>

          {/* Statistics Tab */}
          {activeTab === 'stats' && (
            <StatsTab />
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <section className="content-section">
              <h2>Moderation Reports</h2>
              
              <div className="admin-filters">
                <button
                  className={`filter-chip ${filter === 'all' ? 'active' : ''}`}
                  onClick={() => setFilter('all')}
                >
                  All Reports
                </button>
                <button
                  className={`filter-chip ${filter === 'content' ? 'active' : ''}`}
                  onClick={() => setFilter('content')}
                >
                  Content
                </button>
                <button
                  className={`filter-chip ${filter === 'activity' ? 'active' : ''}`}
                  onClick={() => setFilter('activity')}
                >
                  Activity
                </button>
              </div>

              {loading ? (
                <div className="loading-state">Loading reports...</div>
              ) : reports.length === 0 ? (
                <div className="empty-state">
                  <p>No reports to review.</p>
                </div>
              ) : (
                <div className="reports-list">
                  {reports.map((report) => (
                    <div key={`${report.itemType}-${report.itemId}`} className="report-card">
                      <div className="report-header">
                        <div className="report-info">
                          <span className={`type-badge type-${report.itemType}`}>
                            {report.itemType}
                          </span>
                          <h3>{report.title || 'Untitled'}</h3>
                          {report.isHidden && <span className="hidden-badge">Hidden</span>}
                        </div>
                        <div className="report-count">
                          {report.reportCount} {report.reportCount === 1 ? 'report' : 'reports'}
                        </div>
                      </div>

                      <div className="report-meta">
                        <span>By: {report.authorDisplayName}</span>
                        {report.contentType && <span> • Type: {report.contentType}</span>}
                        {report.activityType && <span> • Type: {report.activityType}</span>}
                      </div>

                      <div className="report-reasons">
                        <h4>Reports:</h4>
                        {report.reports.map((r, idx) => (
                          <div key={idx} className="report-reason">
                            <strong>{r.reporterName}:</strong> {r.reason}
                            <span className="report-date">
                              {new Date(r.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="report-actions">
                        <button
                          className={`btn ${report.isHidden ? 'btn-success' : 'btn-warning'}`}
                          onClick={() => handleHideItem(report.itemId, report.itemType, report.isHidden)}
                        >
                          {report.isHidden ? 'Unhide' : 'Hide'} Item
                        </button>
                        <a
                          href={report.itemType === 'content' 
                            ? `/content/${report.itemId}` 
                            : `/community#${report.itemId}`}
                          className="btn btn-outline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Item
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Content Management Tab */}
          {activeTab === 'content' && (
            <section className="content-section">
              <div className="section-header">
                <h2>Resource Management</h2>
                <Link to="/content/new" className="btn btn-primary">
                  Create New Resource
                </Link>
              </div>
              
              <div className="admin-filters">
                <button
                  className={`filter-chip ${contentFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setContentFilter('all')}
                >
                  All ({content.length})
                </button>
                <button
                  className={`filter-chip ${contentFilter === 'published' ? 'active' : ''}`}
                  onClick={() => setContentFilter('published')}
                >
                  Published
                </button>
                <button
                  className={`filter-chip ${contentFilter === 'hidden' ? 'active' : ''}`}
                  onClick={() => setContentFilter('hidden')}
                >
                  Hidden
                </button>
                <button
                  className={`filter-chip ${contentFilter === 'post' ? 'active' : ''}`}
                  onClick={() => setContentFilter('post')}
                >
                  Posts
                </button>
                <button
                  className={`filter-chip ${contentFilter === 'page' ? 'active' : ''}`}
                  onClick={() => setContentFilter('page')}
                >
                  Pages
                </button>
              </div>

              {contentLoading ? (
                <div className="loading-state">Loading content...</div>
              ) : filteredContent.length === 0 ? (
                <div className="empty-state">
                  <p>No content found.</p>
                </div>
              ) : (
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Type</th>
                        <th>Author</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Views</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredContent.map((item) => (
                        <tr key={item._id}>
                          <td>
                            <Link to={`/content/${item._id}`} className="table-title-link">
                              {item.title}
                            </Link>
                          </td>
                          <td>
                            <span className={`type-badge type-${item.type}`}>
                              {item.type}
                            </span>
                          </td>
                          <td>{item.authorDisplayName}</td>
                          <td>
                            <div className="status-badges">
                              {item.isPublished && (
                                <span className="status-badge published">Published</span>
                              )}
                              {!item.isPublished && (
                                <span className="status-badge draft">Draft</span>
                              )}
                              {item.isHidden && (
                                <span className="status-badge hidden">Hidden</span>
                              )}
                            </div>
                          </td>
                          <td>{formatDate(item.createdAt)}</td>
                          <td>{item.viewCount || 0}</td>
                          <td>
                            <div className="table-actions">
                              <Link to={`/content/${item._id}/edit`} className="btn-icon" title="Edit">
                                ✏️
                              </Link>
                              <button
                                className="btn-icon"
                                onClick={() => handleHideItem(item._id, 'content', item.isHidden)}
                                title={item.isHidden ? 'Unhide' : 'Hide'}
                              >
                                {item.isHidden ? '👁️' : '🙈'}
                              </button>
                              <button
                                className="btn-icon btn-danger"
                                onClick={() => handleDeleteItem(item._id, 'content')}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* Community Management Tab */}
          {activeTab === 'community' && (
            <section className="content-section">
              <h2>Community Management</h2>
              
              <div className="admin-filters">
                <button
                  className={`filter-chip ${activityFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setActivityFilter('all')}
                >
                  All ({activities.length})
                </button>
                <button
                  className={`filter-chip ${activityFilter === 'announcement' ? 'active' : ''}`}
                  onClick={() => setActivityFilter('announcement')}
                >
                  Announcements
                </button>
                <button
                  className={`filter-chip ${activityFilter === 'event' ? 'active' : ''}`}
                  onClick={() => setActivityFilter('event')}
                >
                  Events
                </button>
                <button
                  className={`filter-chip ${activityFilter === 'discussion' ? 'active' : ''}`}
                  onClick={() => setActivityFilter('discussion')}
                >
                  Discussions
                </button>
                <button
                  className={`filter-chip ${activityFilter === 'hidden' ? 'active' : ''}`}
                  onClick={() => setActivityFilter('hidden')}
                >
                  Hidden
                </button>
              </div>

              {activityLoading ? (
                <div className="loading-state">Loading activities...</div>
              ) : filteredActivities.length === 0 ? (
                <div className="empty-state">
                  <p>No activities found.</p>
                </div>
              ) : (
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Type</th>
                        <th>Author</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Reactions</th>
                        <th>Comments</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredActivities.map((item) => (
                        <tr key={item._id}>
                          <td>
                            <Link to={`/community`} className="table-title-link">
                              {item.title}
                            </Link>
                          </td>
                          <td>
                            <span className={`type-badge type-${item.type}`}>
                              {item.type}
                            </span>
                          </td>
                          <td>{item.authorDisplayName}</td>
                          <td>
                            {item.isHidden && (
                              <span className="status-badge hidden">Hidden</span>
                            )}
                          </td>
                          <td>{formatDate(item.createdAt)}</td>
                          <td>{item.reactions?.upvote || 0}</td>
                          <td>{item.commentCount || 0}</td>
                          <td>
                            <div className="table-actions">
                              <button
                                className="btn-icon"
                                onClick={() => handleHideItem(item._id, 'activity', item.isHidden)}
                                title={item.isHidden ? 'Unhide' : 'Hide'}
                              >
                                {item.isHidden ? '👁️' : '🙈'}
                              </button>
                              <button
                                className="btn-icon btn-danger"
                                onClick={() => handleDeleteItem(item._id, 'activity')}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* User Management Tab */}
          {activeTab === 'users' && (
            <section className="content-section">
              <h2>User Management</h2>
              
              <div className="admin-filters">
                <button
                  className={`filter-chip ${userFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setUserFilter('all')}
                >
                  All Users ({users.length})
                </button>
                <button
                  className={`filter-chip ${userFilter === 'admin' ? 'active' : ''}`}
                  onClick={() => setUserFilter('admin')}
                >
                  Admins
                </button>
                <button
                  className={`filter-chip ${userFilter === 'moderator' ? 'active' : ''}`}
                  onClick={() => setUserFilter('moderator')}
                >
                  Moderators
                </button>
                <button
                  className={`filter-chip ${userFilter === 'member' ? 'active' : ''}`}
                  onClick={() => setUserFilter('member')}
                >
                  Members
                </button>
              </div>

              {userLoading ? (
                <div className="loading-state">Loading users...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="empty-state">
                  <p>No users found.</p>
                </div>
              ) : (
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Display Name</th>
                        <th>Email</th>
                        <th>Roles</th>
                        <th>Joined</th>
                        <th>Content</th>
                        <th>Posts</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <strong>{user.displayName}</strong>
                          </td>
                          <td>{user.email}</td>
                          <td>
                            <div className="role-badges">
                              {user.roles && user.roles.map((role) => (
                                <span key={role} className={`role-badge role-${role}`}>
                                  {role}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td>{formatDate(user.createdAt)}</td>
                          <td>{user.contentCount || 0}</td>
                          <td>{user.activityCount || 0}</td>
                          <td>
                            <div className="role-toggle-actions">
                              <label className="role-toggle">
                                <input
                                  type="checkbox"
                                  checked={user.roles && user.roles.includes('admin')}
                                  onChange={() => handleRoleChange(user.id, user.roles || [], 'admin')}
                                />
                                <span>Admin</span>
                              </label>
                              <label className="role-toggle">
                                <input
                                  type="checkbox"
                                  checked={user.roles && user.roles.includes('moderator')}
                                  onChange={() => handleRoleChange(user.id, user.roles || [], 'moderator')}
                                />
                                <span>Moderator</span>
                              </label>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
